/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { ThemeModel } from '../../models';
import { cloudianTheme, darkTheme, defaultTheme } from '../../../../assets/shared/themes';

@Injectable({
    providedIn: 'root'
})

export class ThemeService {
    /**
     * To store the available themes.
     * @private
     */
    private availableThemes: ThemeModel[] = [cloudianTheme, darkTheme, defaultTheme];

    /**
     * To store the company logo file.
     * @private
     */
    private companyLogoFile: any;

    /**
     * Returns all theme names
     */
    public getThemeNames(): any {
        return this.availableThemes.map(theme => theme.name);
    }

    /**
     * Public function to initialise setting the theme.
     * @param themeName
     */
    public setTheme(themeName: string): void {
        const dynamicStyleSheet = document.getElementById('dynamicStyleSheet');
        if (dynamicStyleSheet) {
            dynamicStyleSheet.remove();
        }
        const theme = this.getThemeByName(themeName);
        this.applyTheme(theme);

        if (theme.styleSheetLocation.length) {
            this.appendStylesheet(theme);
        }

        this.setThemeInLocalStorage(theme);
    }

    /**
     * Get theme name from local storage.
     */
    public getThemeNameFromLocalStorage(): string {
        const themeName = JSON.parse(localStorage.getItem('lbTheme'));

        return themeName.name;
    }

    /**
     * Get themes from available themes.
     * @param themeName
     */
    public getThemeByName(themeName: string): ThemeModel {
        for (const theme of this.availableThemes) {
            if (theme.name === themeName && !theme.deactivate) {
                return theme;
            }
        }

        return defaultTheme;
    }

    /**
     * Get image path from theme.
     */
    public getThemeImagePath(): string {
        const theme = this.getThemeByName(this.getThemeNameFromLocalStorage());

        return theme.imagePath;
    }

    /**
     * Set the company logo.
     */
    public setLogo() {
        const theme = this.getThemeFromLocalStorage('lbTheme');
        this.companyLogoFile = theme.includes('dark')
            ? 'light'
            : 'dark';
    }

    /**
     * Apply the selected theme to the HTML tag.
     * @param theme
     * @private
     */
    private applyTheme(theme: ThemeModel): void {
        Object.keys(theme.properties).forEach(property => {
            document.documentElement.style.setProperty(
                property,
                theme.properties[property]
            );
        });
    }

    /**
     * Insert selected stylesheet into the header.
     * @param theme
     * @private
     */
    private appendStylesheet(theme): void {
        const link = document.createElement('link');

        link.type = 'text/css';
        link.id = 'dynamicStyleSheet';
        link.rel = 'stylesheet';
        link.href = theme.styleSheetLocation;

        document.head.appendChild(link);
    }

    /**
     * Set the theme in localStorage.
     * @param theme
     * @private
     */
    private setThemeInLocalStorage(theme): void {
        localStorage.setItem('lbTheme', JSON.stringify({ name: theme.name }));
    }

    /**
     * Get theme from local storage.
     * @param itemName
     * @private
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private getThemeFromLocalStorage(itemName): any {
        return localStorage.getItem('lbTheme');
    }
}
